import {
  Combobox,
  Portal,
  TagsInput,
  useCombobox,
  useFilter,
  useListCollection,
  useTagsInput,
} from '@chakra-ui/react';
import { NavArrowDown } from 'iconoir-react';
import { useEffect, useId, useMemo } from 'react';

export interface FilterTagComboboxItem {
  label: string;
  value: string;
}

interface FilterTagComboboxProps {
  items: FilterTagComboboxItem[];
  value: string[] | null | undefined;
  onChange: (value: string[] | null) => void;
  placeholder: string;
  /** Placeholder once at least one value is selected. */
  searchPlaceholder?: string;
  maxSelected?: number;
  disabled?: boolean;
  /** Tag label override (defaults to the item label). */
  formatTag?: (value: string) => string;
}

/**
 * Generic filterable multi-select: TagsInput control + Combobox suggestions.
 * Used for countries, niches and audience interests. The dropdown renders in
 * a Portal so the panel's internal scroll never clips it.
 */
const FilterTagCombobox = ({
  items,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  maxSelected,
  disabled = false,
  formatTag,
}: FilterTagComboboxProps) => {
  const { contains } = useFilter({ sensitivity: 'base' });

  const { collection, filter, set } = useListCollection({
    initialItems: items,
    filter: contains,
  });

  // Keep the collection in sync when translated labels change.
  useEffect(() => {
    set(items);
  }, [items, set]);

  const uid = useId();
  const selected = value ?? [];

  const tags = useTagsInput({
    ids: { input: `input_${uid}`, control: `control_${uid}` },
    value: selected,
    disabled,
    onValueChange: (details) => {
      onChange(details.value.length > 0 ? details.value : null);
    },
  });

  const combobox = useCombobox({
    ids: { input: `input_${uid}`, control: `control_${uid}` },
    collection,
    disabled,
    onInputValueChange: (e) => filter(e.inputValue),
    value: [],
    openOnClick: true,
    allowCustomValue: false,
    onValueChange: (e) => {
      const next = e.value[0];
      if (!next || selected.includes(next)) return;

      if (maxSelected === 1) {
        tags.setValue([next]);
        onChange([next]);
        return;
      }

      if (maxSelected && selected.length >= maxSelected) return;
      tags.addValue(next);
    },
    selectionBehavior: 'clear',
  });

  const labelByValue = useMemo(
    () => new Map(items.map((item) => [item.value, item.label])),
    [items]
  );
  // Falls back to the raw value so an unknown entry from a bookmarked URL still renders.
  const labelFor = (val: string) => formatTag?.(val) ?? labelByValue.get(val) ?? val;

  return (
    <Combobox.RootProvider value={combobox}>
      <TagsInput.RootProvider value={tags}>
        <TagsInput.Control>
          {tags.value.map((val, index) => (
            <TagsInput.Item key={val} index={index} value={val}>
              <TagsInput.ItemPreview>
                <TagsInput.ItemText>{labelFor(val)}</TagsInput.ItemText>
                <TagsInput.ItemDeleteTrigger />
              </TagsInput.ItemPreview>
            </TagsInput.Item>
          ))}

          <Combobox.Input unstyled asChild>
            <TagsInput.Input
              placeholder={selected.length > 0 ? (searchPlaceholder ?? '') : placeholder}
            />
          </Combobox.Input>
          <Combobox.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              style={{
                background: 'none',
                border: 'none',
                cursor: disabled ? 'default' : 'pointer',
                padding: '0 4px',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                color: '#1a1a1a',
              }}
            >
              <NavArrowDown width={14} height={14} />
            </button>
          </Combobox.Trigger>
        </TagsInput.Control>

        <Portal>
          <Combobox.Positioner>
            <Combobox.Content maxH="240px" overflowY="auto">
              {collection.items.map((item) => (
                <Combobox.Item item={item} key={item.value}>
                  <Combobox.ItemText>{item.label}</Combobox.ItemText>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </TagsInput.RootProvider>
    </Combobox.RootProvider>
  );
};

export default FilterTagCombobox;
